<template>
    <div class="notif-page">

        <!-- Header -->
        <div class="notif-header">
            <div class="notif-header-left">
                <div class="notif-header-icon"><i class="fa fa-bell"></i></div>
                <div>
                    <div class="notif-title">Notifications</div>
                    <div class="notif-sub">
                        <span v-if="unreadCount > 0" class="unread-pill">{{ unreadCount }} unread</span>
                        <span v-else class="all-read-pill"><i class="fa fa-check-circle"></i> All caught up</span>
                    </div>
                </div>
            </div>
            <button v-if="unreadCount > 0" class="mark-all-btn" :disabled="markingAll" @click="markAllSeen">
                <i class="fa fa-check-double"></i>
                {{ markingAll ? 'Marking…' : 'Mark all as read' }}
            </button>
        </div>

        <!-- Filters -->
        <div class="filter-bar">
            <button v-for="f in filters" :key="f.value" :class="['filter-btn', { active: activeFilter === f.value }]"
                @click="switchFilter(f.value)">
                {{ f.label }}
                <span v-if="f.count !== null" class="filter-count">{{ f.count }}</span>
            </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
            <i class="fa fa-spinner spin"></i>
            <span>Loading notifications…</span>
        </div>

        <!-- Empty -->
        <div v-else-if="filteredNotifications.length === 0" class="empty-state">
            <div class="empty-icon"><i class="fa fa-bell-slash"></i></div>
            <div class="empty-title">No notifications</div>
            <div class="empty-sub">You're all caught up! Check back later.</div>
        </div>

        <!-- Timeline List -->
        <div v-else class="timeline">
            <div v-for="(notif, index) in filteredNotifications" :key="notif.id"
                :class="['timeline-item', { unread: !notif.seen_status || notif.seen_status === 'unseen' }]">
                <!-- Chain line + dot -->
                <div class="timeline-spine">
                    <div class="spine-line spine-line-top" :class="{ invisible: index === 0 }"></div>
                    <div
                        :class="['spine-dot', (!notif.seen_status || notif.seen_status === 'unseen') ? 'dot-unread' : 'dot-read']">
                        <i :class="getTypeIcon(notif.type)" class="dot-icon"></i>
                    </div>
                    <div class="spine-line spine-line-bottom"
                        :class="{ invisible: index === filteredNotifications.length - 1 }"></div>
                </div>

                <!-- Card -->
                <div class="timeline-card">
                    <!-- Card top row -->
                    <div class="card-top">
                        <div class="card-top-left">
                            <span class="notif-subject">{{ notif.subject || 'Notification' }}</span>
                            <span v-if="notif.type" :class="['type-badge', getTypeColor(notif.type)]">
                                {{ formatType(notif.type) }}
                            </span>
                        </div>
                        <div class="card-top-right">
                            <span class="notif-time"><i class="fa fa-clock"></i> {{ notif.created_at }}</span>
                            <span v-if="!notif.seen_status || notif.seen_status === 'unseen'" class="unread-dot"></span>
                        </div>
                    </div>

                    <!-- Short description -->
                    <div v-if="notif.short_description" class="notif-short">
                        {{ notif.short_description }}
                    </div>

                    <!-- Full content (HTML) -->
                    <div v-if="notif.content" class="notif-content" v-html="notif.content"></div>

                    <!-- Actions -->
                    <div class="card-actions">

                        <button v-if="!notif.seen_status || notif.seen_status === 'unseen'" class="mark-read-btn"
                            @click.stop="markSeen(notif)">
                            <i class="fa fa-check"></i> Mark as read
                        </button>
                        <span v-else class="seen-label">
                            <i class="fa fa-check-double"></i> Read
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <Pagination :pagination="pagination" @page-change="changePage" />

    </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import Pagination from '@/components/utils/PaginationBar.vue'

export default {
    name: 'NotificationList',
    components: { Pagination },

    data() {
  return {
    notifications: [],
    unreadCount:   0,
    loading:       false,
    markingAll:    false,
    activeFilter:  'all',
    pagination: {
      total_rows:   0,
      per_page:     20,
      current_page: 1,
      total_pages:  1,
    },
  }
},

    computed: {
     filters() {
    return [
      { label: 'All',    value: 'all',    count: this.pagination?.total_rows ?? 0 },
      { label: 'Unread', value: 'unread', count: this.unreadCount },
      { label: 'Read',   value: 'read',   count: null },
    ]
  },
        filteredNotifications() {
            if (this.activeFilter === 'unread')
                return this.notifications.filter(n => !n.seen_status || n.seen_status === 'unseen')
            if (this.activeFilter === 'read')
                return this.notifications.filter(n => n.seen_status === 'seen')
            return this.notifications
        }
    },

    mounted() {
        this.fetchNotifications()
    },

    methods: {
       async fetchNotifications() {
  this.loading = true

  const result = await form_api_submit({
    url:    'notifications',
    method: 'GET',
    data:   { page: this.pagination.current_page },
    show_success_toast: false,
    show_error_toast:   true,
  })

  if (result.success) {
    const d = result.data

    // Generic::paginate returns list + pagination keys at the same level
    this.notifications = (d.list || []).map(n => ({
      ...n,
      created_at: this.formatDate(n.created_at)
    }))
    this.unreadCount = d.unread_count ?? 0
    this.pagination  = d.pagination ?? {
      total_rows:   0,
      per_page:     20,
      current_page: 1,
      total_pages:  1,
    }
  }

  this.loading = false
},

formatDate(raw) {
  if (!raw) return ''
  const date = new Date(raw)
  if (isNaN(date)) return raw 
  const now  = new Date()
  const diff = Math.floor((now - date) / 1000)  // seconds

  if (diff < 60)   return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) === 1 ? '' : 's'} ago`
  if (diff < 2592000) return `${Math.floor(diff / 604800)} week${Math.floor(diff / 604800) === 1 ? '' : 's'} ago`

  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
},
        changePage(page) {
            if (page < 1 || page > this.pagination.total_pages) return
            this.pagination.current_page = page
            this.fetchNotifications()
        },

        switchFilter(val) {
            this.activeFilter = val
            this.pagination.current_page = 1
            this.fetchNotifications()
        },

        async markSeen(notif) {
            const result = await form_api_submit({
                url: `notifications/${notif.id}/seen`,
                method: 'POST',
                show_success_toast: true,
            })

            if (result.success) {
                notif.seen_status = 'seen'
                this.unreadCount = Math.max(0, this.unreadCount - 1)
            }
        },

        async markAllSeen() {
            this.markingAll = true

            const result = await form_api_submit({
                url: 'notifications/mark-all-seen',
                method: 'POST',
                show_success_toast: true,
            })

            if (result.success) {
                this.notifications.forEach(n => { n.seen_status = 'seen' })
                this.unreadCount = 0
            }

            this.markingAll = false
        },

        getTypeIcon(type) {
            const map = {
                alert: 'fa fa-exclamation-triangle',
                info: 'fa fa-info-circle',
                success: 'fa fa-check-circle',
                warning: 'fa fa-exclamation-circle',
                order: 'fa fa-file-invoice',
                system: 'fa fa-cog',
                reminder: 'fa fa-clock',
                notification: 'fa fa-bell',
            }
            return map[type?.toLowerCase()] || 'fa fa-bell'
        },

        getTypeColor(type) {
            const map = {
                alert: 'type-red',
                info: 'type-blue',
                success: 'type-green',
                warning: 'type-amber',
                order: 'type-purple',
                system: 'type-gray',
                reminder: 'type-teal',
                notification: 'type-blue',
            }
            return map[type?.toLowerCase()] || 'type-blue'
        },

        formatType(type) {
            if (!type) return ''
            return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
        }
    }
}
</script>

<style scoped>
.notif-page {
    max-width: 780px;
    margin: 0 auto;
    padding: 1.5rem 1rem 2rem;
    font-family: 'Inter', system-ui, sans-serif;
}

/* ── Header ─────────────────────────────────────────────── */
.notif-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
    gap: 0.75rem;
}

.notif-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.notif-header-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #E6F1FB;
    display: flex;
    align-items: center;
    justify-content: center;
}

.notif-header-icon i {
    font-size: 18px;
    color: var(--pp-accent);
}

.notif-title {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
}

.unread-pill {
    display: inline-flex;
    align-items: center;
    background: #FAEEDA;
    color: #854F0B;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 20px;
    margin-top: 3px;
}

.all-read-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #0F6E56;
    font-size: 11px;
    font-weight: 500;
    margin-top: 3px;
}

.mark-all-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    padding: 7px 16px;
    border-radius: 20px;
    background: #fff;
    color: var(--pp-accent);
    border: 1px solid #B5D4F4;
    cursor: pointer;
    transition: all 0.15s;
}

.mark-all-btn:hover:not(:disabled) {
    background: #E6F1FB;
}

.mark-all-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* ── Filters ─────────────────────────────────────────────── */
.filter-bar {
    display: flex;
    gap: 6px;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
}

.filter-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    background: #fff;
    color: #64748b;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.15s;
}

.filter-btn:hover {
    border-color: var(--pp-accent);
    color: var(--pp-accent);
}

.filter-btn.active {
    background: var(--pp-accent);
    color: #fff;
    border-color: var(--pp-accent);
}

.filter-count {
    background: rgba(255, 255, 255, 0.25);
    padding: 1px 7px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
}

.filter-btn:not(.active) .filter-count {
    background: #f1f5f9;
    color: #64748b;
}

/* ── States ──────────────────────────────────────────────── */
.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 3rem;
    color: #64748b;
    font-size: 13px;
}

.empty-state {
    text-align: center;
    padding: 4rem 2rem;
}

.empty-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
}

.empty-icon i {
    font-size: 24px;
    color: #94a3b8;
}

.empty-title {
    font-size: 15px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 4px;
}

.empty-sub {
    font-size: 13px;
    color: #94a3b8;
}

/* ── Timeline ────────────────────────────────────────────── */
.timeline {
    display: flex;
    flex-direction: column;
}

.timeline-item {
    display: flex;
    gap: 0;
    position: relative;
}

/* ── Spine ───────────────────────────────────────────────── */
.timeline-spine {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 40px;
    flex-shrink: 0;
}

.spine-line {
    width: 2px;
    flex: 1;
    min-height: 12px;
    background: #e2e8f0;
}

.spine-line.invisible {
    background: transparent;
}

.spine-dot {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    z-index: 1;
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px #e2e8f0;
}

.spine-dot.dot-unread {
    background: var(--pp-accent);
    box-shadow: 0 0 0 2px #B5D4F4;
}

.spine-dot.dot-read {
    background: #e2e8f0;
}

.dot-icon {
    font-size: 12px;
    color: #fff;
}

.dot-read .dot-icon {
    color: #94a3b8;
}

/* ── Card ────────────────────────────────────────────────── */
.timeline-card {
    flex: 1;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 14px 16px;
    margin: 0 0 12px 12px;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.timeline-item.unread .timeline-card {
    background: #f8fbff;
    border-color: #B5D4F4;
}

.timeline-card:hover {
    border-color: #93c5fd;
    box-shadow: 0 2px 12px rgba(24, 95, 165, 0.07);
}

.card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 6px;
}

.card-top-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.card-top-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.notif-subject {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
}

.notif-time {
    font-size: 11px;
    color: #94a3b8;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
}

.unread-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--pp-accent);
    flex-shrink: 0;
}

.type-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 20px;
    white-space: nowrap;
}

/* Short description */
.notif-short {
    font-size: 12px;
    color: #64748b;
    margin-bottom: 6px;
    font-style: italic;
}

/* Full HTML content */
.notif-content {
    font-size: 13px;
    color: #334155;
    line-height: 1.6;
    padding: 10px 12px;
    background: #f8fafc;
    border-left: 3px solid #B5D4F4;
    border-radius: 0 6px 6px 0;
    margin-bottom: 10px;
}

/* Allow HTML tags from content */
.notif-content :deep(strong) {
    font-weight: 600;
    color: #1e293b;
}

.notif-content :deep(b) {
    font-weight: 600;
    color: #1e293b;
}

.notif-content :deep(br) {
    display: block;
    margin: 2px 0;
}

/* ── Actions ─────────────────────────────────────────────── */
.card-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 4px;
}

.notif-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    text-decoration: none;
    padding: 3px 10px;
    border-radius: 6px;
    transition: background 0.15s;
}

.link-view {
    background: #E6F1FB;
    color: var(--pp-accent);
}

.link-view:hover {
    background: #B5D4F4;
}

.link-details {
    background: #EEEDFE;
    color: #534AB7;
}

.link-details:hover {
    background: #d5d4fc;
}

.mark-read-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    padding: 3px 10px;
    border-radius: 6px;
    background: transparent;
    color: #64748b;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.15s;
}

.mark-read-btn:hover {
    border-color: #0F6E56;
    color: #0F6E56;
    background: #E1F5EE;
}

.seen-label {
    font-size: 11px;
    color: #94a3b8;
    display: flex;
    align-items: center;
    gap: 4px;
}

/* ── Type colours ─────────────────────────────────────────── */
.type-blue {
    background: #E6F1FB;
    color: var(--pp-accent);
}

.type-red {
    background: #FCEBEB;
    color: #A32D2D;
}

.type-green {
    background: #E1F5EE;
    color: #0F6E56;
}

.type-amber {
    background: #FAEEDA;
    color: #854F0B;
}

.type-purple {
    background: #EEEDFE;
    color: #534AB7;
}

.type-gray {
    background: #f1f5f9;
    color: #64748b;
}

.type-teal {
    background: #E1F5EE;
    color: #0D7377;
}

/* ── Spin ─────────────────────────────────────────────────── */
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.spin {
    display: inline-block;
    animation: spin 0.8s linear infinite;
}

@media (max-width: 560px) {
    .card-top {
        flex-direction: column;
    }

    .card-top-right {
        flex-direction: row;
    }

    .timeline-spine {
        width: 28px;
    }

    .spine-dot {
        width: 26px;
        height: 26px;
    }
}
</style>